import { FC, useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  DataTable,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectRow,
} from '@carbon/react';
import { Reset } from '@carbon/react/icons';
import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import type { WellsResponse } from 'types/api-responses';
import styles from './structuring-module.module.scss';

// Real-API existing-well picker for Add Well / Change Match. Reuses the shared
// `Query::Wells` search resource via `searchAtomPair('wells')` — the same
// resource the production Wells search page uses. Single-select (radio) row
// model so the parent can post `well_id`.

interface Props {
  selectedWellId: number | null;
  /** Seed the search box (e.g. the staged well name for Change Match). */
  initialSearchValue?: string;
  onSelect: (well: WellsResponse) => void;
}

const HEADERS = [
  { key: 'select', header: '' },
  { key: 'name', header: 'Well Name' },
  { key: 'uwi', header: 'UWI / API' },
  { key: 'status', header: 'Well Status' },
  { key: 'holeDirection', header: 'Hole Direction' },
  { key: 'currentOperator', header: 'Current Operator' },
  { key: 'permitDate', header: 'Permit Date' },
  { key: 'spudDate', header: 'Spud Date' },
  { key: 'firstProductionDate', header: 'First Production Date' },
];

const PAGE_SIZE = 25;

export const WellsTableWithFilters: FC<Props> = ({
  selectedWellId,
  initialSearchValue = '',
  onSelect,
}) => {
  // Each mount gets its own search atom pair so concurrent pickers (Add Well /
  // Change Match) don't share result state.
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () =>
      searchAtomPair<WellsResponse, SearchParams>('wells', {
        text: initialSearchValue,
        page: 1,
        pageSize: PAGE_SIZE,
        paginated: true,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [searchState, runSearch] = useAtom(searchAtom);
  const resetSearch = useSetAtom(resetSearchDataAtom);
  const [text, setText] = useState(initialSearchValue);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Debounce ONLY the text search. A text change resets to page 1 and
  // re-searches after 300ms (keeping the currently-selected page size). Page
  // and page-size navigation is driven solely by the Pagination `onChange`
  // handler below, so changing the page size fires exactly one request.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setPage(1);
      runSearch(() => ({
        text,
        page: 1,
        pageSize,
        paginated: true,
      }));
    }, 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(
    () => () => resetSearch({ unsetCurrentPayload: true }),
    [resetSearch]
  );

  const results = searchState.data?.results ?? [];
  const totalCount = searchState.data?.totalCount ?? results.length;

  const rows = results.map((w) => ({
    id: String(w.id),
    name: w.name || w.leaseName || '—',
    uwi: w.uwi || '—',
    status: w.status || '—',
    holeDirection: w.holeDirection || '—',
    currentOperator: w.currentOperator || '—',
    permitDate: w.permitDate || '—',
    spudDate: w.spudDate || '—',
    firstProductionDate: w.firstProductionDate || '—',
    select: '',
  }));

  const wellById = (id: string) => results.find((w) => String(w.id) === id);

  return (
    <div className={styles.wellPickerTable}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.25rem' }}>
        <Search
          size="lg"
          labelText="Search wells"
          placeholder="Search by UWI / API or well name"
          value={text}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setText(e.target.value)
          }
        />
        {/* Reset the picker: clear the search box and return to the first
            page at the default page size. */}
        <Button
          kind="ghost"
          hasIconOnly
          renderIcon={Reset}
          iconDescription="Reset Table"
          tooltipPosition="bottom"
          tooltipAlignment="end"
          onClick={() => {
            const textWasEmpty = text === '';
            setText('');
            setPage(1);
            setPageSize(PAGE_SIZE);
            // Clearing a non-empty search re-runs via the debounce effect (it
            // picks up the just-reset page size). Only search explicitly when
            // text was already empty so the effect wouldn't fire — this avoids
            // a duplicate request.
            if (textWasEmpty) {
              runSearch(() => ({
                text: '',
                page: 1,
                pageSize: PAGE_SIZE,
                paginated: true,
              }));
            }
          }}
        />
      </div>
      <TableContainer>
        <DataTable rows={rows} headers={HEADERS} isSortable>
          {({ rows: dtRows, headers, getHeaderProps, getRowProps }: any) => (
            <Table size="md">
              <TableHead>
                <TableRow>
                  {headers.map((header: any) => {
                    return header.key === 'select' ? (
                      <TableHeader key="select" />
                    ) : (
                      <TableHeader
                        {...getHeaderProps({ header })}
                        key={header.key}
                      >
                        {header.header}
                      </TableHeader>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {dtRows.map((row: any) => {
                  const isSelected = String(selectedWellId) === row.id;
                  return (
                    <TableRow
                      {...getRowProps({ row })}
                      key={row.id}
                      onClick={() => {
                        const w = wellById(row.id);
                        if (w) onSelect(w);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.cells.map((cell: any) => {
                        return cell.info.header === 'select' ? (
                          <TableSelectRow
                            key={cell.id}
                            id={`well-pick-${row.id}`}
                            name="afe-well-pick"
                            checked={isSelected}
                            radio
                            ariaLabel={`Select well ${row.id}`}
                            onSelect={() => {
                              const w = wellById(row.id);
                              if (w) onSelect(w);
                            }}
                          />
                        ) : (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DataTable>
        {searchState.loading && (
          <p className={styles.wellPickerLoading}>Searching…</p>
        )}
        {!searchState.loading && rows.length === 0 && (
          <p className={styles.wellPickerEmpty}>No wells found.</p>
        )}
      </TableContainer>
      <Pagination
        totalItems={totalCount}
        pageSize={pageSize}
        pageSizes={[25, 50, 100]}
        page={page}
        onChange={({
          page: newPage,
          pageSize: newSize,
        }: {
          page: number;
          pageSize: number;
        }) => {
          setPage(newPage);
          setPageSize(newSize);
          runSearch(() => ({
            text,
            page: newPage,
            pageSize: newSize,
            paginated: true,
          }));
        }}
      />
    </div>
  );
};
